import React from "react";
import { Button } from "@heroui/button";
import {
  Loader2,
  Package,
  Archive,
  Clock,
  Edit,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

import {
  InventoryItem,
  getStockStatus,
  getStatusColor,
} from "../../types/inventory";

interface InventoryTableProps {
  items: InventoryItem[];
  loading: boolean;
  onEdit: (itemId: number) => void;
  onAdjustStock: (item: InventoryItem) => void;
  onViewHistory: (itemId: number) => void;
  onToggleStatus: (item: InventoryItem) => Promise<void>;
}

const InventoryTable: React.FC<InventoryTableProps> = ({
  items,
  loading,
  onEdit,
  onAdjustStock,
  onViewHistory,
  onToggleStatus,
}) => {
  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount / 100); // Assuming amount is in cents
  };

  // Calculate stock percentage
  const calculateStockPercentage = (
    current: string,
    minimum: string,
  ): number => {
    const currentVal = parseFloat(current);
    const minimumVal = parseFloat(minimum);

    if (minimumVal === 0) return 100;

    return (currentVal / minimumVal) * 100;
  };

  // Loading state
  if (loading && items.length === 0) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="inline-block w-8 h-8 animate-spin mb-4" />
        <p className="text-gray-500 dark:text-gray-400">
          Loading inventory items...
        </p>
      </div>
    );
  }

  // Empty state
  if (!loading && items.length === 0) {
    return (
      <div className="p-8 text-center">
        <Package className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
        <p className="text-xl font-semibold text-gray-600 dark:text-gray-300 mb-2">
          No inventory items found
        </p>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Create your first inventory item or adjust your search filters.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Item
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Unit
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Current Stock
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Min. Stock
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Cost
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Status
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {items.map((item) => {
            const stockPercentage = calculateStockPercentage(
              item.current_stock,
              item.minimum_stock,
            );
            const stockStatus = getStockStatus(
              parseFloat(item.current_stock),
              parseFloat(item.minimum_stock),
            );
            const statusColor = getStatusColor(stockStatus);

            return (
              <tr
                key={item.id}
                className={
                  !item.is_active ? "bg-gray-50 dark:bg-gray-900/30" : ""
                }
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {item.name}
                  </div>
                  {item.code && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Code: {item.code}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {item.unit}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {item.current_stock} {item.unit}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {item.minimum_stock} {item.unit}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatCurrency(item.cost_per_unit)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mr-2">
                      <div
                        className={`h-2.5 rounded-full ${
                          stockPercentage <= 25
                            ? "bg-red-500"
                            : stockPercentage <= 75
                              ? "bg-yellow-500"
                              : "bg-green-500"
                        }`}
                        style={{ width: `${Math.min(100, stockPercentage)}%` }}
                      />
                    </div>
                    <span className={`text-xs ${statusColor}`}>
                      {Math.round(stockPercentage)}%
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  <Button
                    color="primary"
                    size="sm"
                    variant="light"
                    onPress={() => onAdjustStock(item)}
                  >
                    <Archive className="w-4 h-4 mr-2" /> Adjust
                  </Button>
                  <Button
                    color="default"
                    size="sm"
                    variant="light"
                    onPress={() => onViewHistory(item.id)}
                  >
                    <Clock className="w-4 h-4 mr-2" /> History
                  </Button>
                  <Button
                    color="primary"
                    size="sm"
                    variant="light"
                    onPress={() => onEdit(item.id)}
                  >
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button
                    color={item.is_active ? "danger" : "success"}
                    size="sm"
                    variant="light"
                    onPress={() => onToggleStatus(item)}
                  >
                    {item.is_active ? (
                      <ToggleLeft className="w-4 h-4 mr-2" />
                    ) : (
                      <ToggleRight className="w-4 h-4 mr-2" />
                    )}
                    {item.is_active ? "Deactivate" : "Activate"}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default InventoryTable;
